;(function () {
  angular
    .module('contrast.settings.password', [
      'ngRoute',
      'ngAnimate',
      'route-segment',
      'view-segment',
      'ui.bootstrap'
    ])
    .controller('PasswordSettingsCtrl', PasswordSettingsCtrl)

  function PasswordSettingsCtrl(
    $q,
    $uibModal,
    $scope,
    $translate,
    logger,
    PasswordFactory,
    ErrorService,
    PasswordService,
    preferencesService,
    SecurityFactory,
    passwordStrength,
    passwordExpirationInterval,
    inactiveAccountExpirationInterval,
    idleTimeoutInterval,
    sessionTimeoutInterval,
    PREFERENCE_PROPERTIES
  ) {
    'ngInject'

    var vm = this

    angular.extend(vm, {
      policy: {},
      restrictions: {},
      timeouts: { idle: 0, absolute: 0 },
      passwordPolicyConstraintsPromise: null,
      userPasswordStrength: {}, // Set to interval constant in init()
      passwordStrengths: [], // An array of passwordStrength with added displayString attribute
      passwordExpiration: null, // Set to interval constant in init()
      passwordExpirationIntervals: _.values(passwordExpirationInterval),
      inactiveAccountExpiration: null, // Set to interval constant in init()
      inactiveAccountExpirationIntervals: _.values(
        inactiveAccountExpirationInterval
      ),
      idleTimeout: null, // Set to interval constant in init()
      idleTimeoutIntervals: _.values(idleTimeoutInterval),
      sessionTimeout: null, // Set to interval constant in init()
      sessionTimeoutIntervals: _.values(sessionTimeoutInterval),
      policyUpdate: { enabled: false },
      minLengthInputDescription: $translate.instant('title_characters'),
      loginLockoutInputDescription: $translate.instant('title_attempts')
    })

    angular.extend(vm, {
      save: save,
      updatePolicy: updatePolicy,
      passwordStrengthChanged: passwordStrengthChanged,
      editPasswordPolicy: editPasswordPolicy
    })

    function _init() {
      angular.forEach(_.values(passwordStrength), function (pwStrength) {
        vm.passwordStrengths.push(pwStrength)
      })

      var p = []
      p.push(
        _getPasswordPolicy(),
        _getPasswordPolicyConstraints(),
        _getTimeouts()
      )
      return $q.all(p)
    }

    function save() {
      $q.all([updatePolicy(), _updateTimeouts()]).then(function () {
        $scope.form.$setPristine()
      })
    }

    function passwordStrengthChanged(newStrength, oldStrengthName) {
      if (newStrength.name === passwordStrength.CUSTOM.name) {
        editPasswordPolicy().catch(function () {
          vm.userPasswordStrength = _.find(passwordStrength, {
            name: oldStrengthName
          })
        })
      } else {
        vm.userPasswordStrength = newStrength
        vm.policy.minLower = vm.userPasswordStrength.req.min_password_lower
        vm.policy.minNumber = vm.userPasswordStrength.req.min_password_numbers
        vm.policy.minSymbols = vm.userPasswordStrength.req.min_password_symbols
        vm.policy.minUpper = vm.userPasswordStrength.req.min_password_upper
      }
    }

    function editPasswordPolicy() {
      return _launchModal().result.then(function (result) {
        vm.userPasswordStrength = passwordStrength.CUSTOM
        vm.policy = result
        updatePolicy()
        vm.userPasswordStrength.$pristine = true
      })
    }

    function _launchModal() {
      return $uibModal.open({
        templateUrl: 'views/common/modal_custom_password_policy.html',
        controller: 'ModalCustomPasswordPolicyCtrl',
        size: 'md-sm',
        resolve: {
          policy: function () {
            return vm.policy
          }
        }
      })
    }

    function _getEnableOverridePropertyValue() {
      vm.passwordPolicyOverridePromise = preferencesService
        .getBooleanValue(PREFERENCE_PROPERTIES.PASSWORD_POLICY_OVERRIDE)
        .then(function (preferenceValue) {
          vm.policyUpdate.enabled = preferenceValue
        })
    }

    function _getPasswordPolicy() {
      vm.passwordPolicyPromise = PasswordFactory.getPolicy()
        .then(function (response) {
          var data = response.data
          vm.policy = PasswordService.buildPolicy(data.policy)
          _getEnableOverridePropertyValue()

          vm.policy.passwordHistory =
            vm.policy.passwordHistory === 'true' ? true : false
          vm.policy.checkMinimumChangeTime =
            vm.policy.checkMinimumChangeTime === 'true' ? true : false
          vm.userPasswordStrength = _policyToPasswordStrength(vm.policy)
          vm.inactiveAccountExpiration = _valueToInterval(
            vm.policy.lockUnusedAccounts,
            inactiveAccountExpirationInterval,
            'days'
          )
          vm.passwordExpiration = _valueToInterval(
            vm.policy.passwordExpirationDays,
            passwordExpirationInterval,
            'days'
          )
        })
        .catch(ErrorService.processError)

      return vm.passwordPolicyPromise
    }

    function _getPasswordPolicyConstraints() {
      vm.passwordPolicyConstraintsPromise = PasswordFactory.getRestrictions()
        .then(function (response) {
          var data = response.data
          vm.restrictions = data.restrictions
          vm.inactiveAccountExpirationIntervals = _.values(
            inactiveAccountExpirationInterval
          ).filter(
            ({ value }) => value <= data.restrictions.max_unused_accounts_days
          )
        })
        .catch(ErrorService.processError)

      return vm.passwordPolicyConstraintsPromise
    }

    function updatePolicy() {
      vm.policy.lockUnusedAccounts = vm.inactiveAccountExpiration.value
      vm.policy.passwordExpirationDays = vm.passwordExpiration.value

      return PasswordFactory.updatePolicy(
        PasswordService.convertToPasswordPolicy(vm.policy)
      )
        .then(function (response) {
          logger.logSuccess(response.data.messages[0])
        })
        .catch(ErrorService.processError)
    }

    function _getTimeouts() {
      return SecurityFactory.getTimeouts()
        .then(function (response) {
          var data = response.data
          vm.timeouts.idle = data.idle_timeout
          vm.timeouts.absolute = data.absolute_timeout
          vm.timeouts.min_idle = data.min_idle_timeout
          vm.timeouts.min_absolute = data.min_absolute_timeout
          vm.timeouts.max_idle = data.max_idle_timeout
          vm.timeouts.max_absolute = data.max_absolute_timeout

          vm.idleTimeout = _valueToInterval(
            vm.timeouts.idle,
            idleTimeoutInterval,
            'minutes'
          )
          vm.sessionTimeout = _valueToInterval(
            vm.timeouts.absolute,
            sessionTimeoutInterval,
            'minutes'
          )
        })
        .catch(ErrorService.processError)
    }

    function _updateTimeouts() {
      return SecurityFactory.updateTimeouts(
        vm.sessionTimeout.value,
        vm.idleTimeout.value
      ).catch(ErrorService.processError)
    }

    function _valueToInterval(value, intervals, units) {
      var obj = _.find(intervals, { value: value })
      if (!obj && value) {
        // Allow legacy or API set values - however only defined intervals will be available when making changes
        obj = { value: value, label: value + ' (' + units + ')' }
      }
      return obj
    }

    function _policyToPasswordStrength(policy) {
      var pwStrength = _.find(vm.passwordStrengths, function (ps) {
        return (
          ps.req.min_password_lower === policy.minLower &&
          ps.req.min_password_numbers === policy.minNumber &&
          ps.req.min_password_symbols === policy.minSymbols &&
          ps.req.min_password_upper === policy.minUpper
        )
      })
      if (!pwStrength) {
        pwStrength = passwordStrength.CUSTOM
        pwStrength.req.min_password_lower = policy.minLower
        pwStrength.req.min_password_numbers = policy.minNumber
        pwStrength.req.min_password_symbols = policy.minSymbols
        pwStrength.req.min_password_upper = policy.minUpper
      }
      return pwStrength
    }

    _init()
  }
})()
